import { useEffect, useState } from 'react';

/**
 * Whether crops render with their surrounding shelf and the model's bounds drawn.
 *
 * This is a module-level store rather than a hook-local state because the setting is
 * read deep in the tree (every CropThumbnail) and by the preloaders, which are not
 * components — prop-drilling it through UPCColumnView would touch every call site.
 * It mirrors the zoom toggle: persisted per browser, off by default.
 *
 * Context is restricted to internal users for now, so the pages publish what the
 * current user is allowed to do and every read is gated on it.
 */
const CROP_CONTEXT_STORAGE_KEY = 'forge-crop-context-enabled';

let cropContextEnabled = readStoredPreference();
let cropContextAllowed = false;
const listeners = new Set<() => void>();

function readStoredPreference(): boolean {
  try {
    return window.localStorage.getItem(CROP_CONTEXT_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function notify() {
  listeners.forEach((listener) => listener());
}

/** True only when the user may use context AND has turned it on. */
export function isCropContextViewEnabled(): boolean {
  return cropContextAllowed && cropContextEnabled;
}

/** Whether the current user may use the context view at all. */
export function isCropContextViewAllowed(): boolean {
  return cropContextAllowed;
}

/** Publish the current user's access; pages call this with their internal-access flag. */
export function setCropContextViewAllowed(allowed: boolean) {
  if (cropContextAllowed === allowed) return;
  cropContextAllowed = allowed;
  notify();
}

export function toggleCropContextView() {
  if (!cropContextAllowed) return;
  cropContextEnabled = !cropContextEnabled;
  try {
    window.localStorage.setItem(CROP_CONTEXT_STORAGE_KEY, String(cropContextEnabled));
  } catch {
    // A browser that refuses storage still gets the toggle for this session.
  }
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function useCropContextStore<T>(read: () => T): T {
  const [value, setValue] = useState(read);
  useEffect(() => subscribe(() => setValue(read())), [read]);
  return value;
}

/** Subscribe to the effective setting (allowed AND on). */
export function useCropContextViewEnabled(): boolean {
  return useCropContextStore(isCropContextViewEnabled);
}

/** Subscribe to whether the toggle should be offered at all. */
export function useCropContextViewAllowed(): boolean {
  return useCropContextStore(isCropContextViewAllowed);
}

/** Test seam: reset the store between tests. */
export function resetCropContextViewForTests() {
  cropContextEnabled = false;
  cropContextAllowed = false;
  listeners.clear();
}
